import React, { useMemo } from 'react'

import { FrameList } from './FrameList'

import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
} from '@sketch/components'

import {
  Trigger,
  CurrentFrame,
  Chevrons,
  Container,
} from './FrameSelector.styles'

import {
  useGetFramesForPageQuery,
  ArtboardDetailInfoFragment,
  FrameDetailInfoFragment,
} from '@sketch/gql-types'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

interface FrameSelectorProps {
  shareIdentifier: string
  versionShortId: string
  pageUUID: string
  currentFrameUUID: string
  currentFrame?: ArtboardDetailInfoFragment | FrameDetailInfoFragment
}

export const FrameSelector: React.FC<FrameSelectorProps> = ({
  shareIdentifier,
  versionShortId,
  pageUUID,
  currentFrameUUID,
  currentFrame,
}) => {
  const { data, fetchMore } = useGetFramesForPageQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      pageUUID,
    },
  })

  const frames = useMemo(
    () => data?.share?.version?.document?.page?.frames?.entries ?? [],
    [data?.share?.version?.document?.page?.frames?.entries]
  )
  const after =
    data?.share?.version?.document?.page?.frames?.meta?.after ?? null
  const totalCount =
    data?.share?.version?.document?.page?.frames?.meta?.totalCount ?? 0

  const remainingItems = totalCount - frames.length

  const isMobile = !useBreakpoint('sm')

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: FrameList,
    dropdownProps: {
      frames,
      after,
      fetchMore,
      shareIdentifier,
      currentFrameUUID,
      remainingFramesToLoad: remainingItems,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
    offset: [-8, 13], // 13 = 5 (usual offset) + 8 (canvas panel bottom padding)
  })

  const isDisabled = frames.length <= 1

  return (
    <Container>
      <Trigger
        {...buttonProps}
        aria-label="Canvas frames list"
        data-testid="frames-selector-trigger"
        disabled={isDisabled}
      >
        <CurrentFrame>
          {currentFrame
            ? currentFrame.name && <SFSymbolLabel text={currentFrame.name} />
            : 'Select frame...'}
        </CurrentFrame>
        {!isDisabled && <Chevrons />}
      </Trigger>
      {!isDisabled && content}
    </Container>
  )
}
